import { Badge, Box, Flex, Text } from '@chakra-ui/react'
import React from 'react'
import { ActionItemFieldsFragment } from '~graphql/generated/graphql'
import OfficeTaskActions from '~components/pending-actions/pending-item-rows/office-task/office-task-actions'
import {
    getOfficeTaskCallbackPhone,
    getOfficeTaskPriorityColor,
    parseOfficeTaskDescription,
} from '~components/pending-actions/pending-item-rows/office-task/office-task.helpers'
import { formatHumanReadableDate } from '~components/chats/chat/messages-list/message-content/message-helpers'
import { useClinic } from '~contexts/clinic-context'
import { cleanPhone } from '~utils/helpers'
import { formatPhoneReadable } from '~utils/phone'

interface Props {
    actionItem: ActionItemFieldsFragment
}

export default function OfficeTaskContent({ actionItem }: Props) {
    const { clinic } = useClinic()
    const parsedOfficeTask = parseOfficeTaskDescription(actionItem.description)
    const callbackPhone = getOfficeTaskCallbackPhone(actionItem)
    const formattedCallbackPhone = callbackPhone
        ? formatPhoneReadable(callbackPhone, clinic?.address?.country) ||
          callbackPhone
        : undefined

    return (
        <Box w="full">
            <Flex
                justify="space-between"
                alignItems="center"
                w="full"
                direction={{ base: 'column', md: 'row' }}
                gap={3}
            >
                <Box flex={1}>
                    <Flex align="center" gap={2} mb={0.5} wrap="wrap">
                        <Text fontSize="sm" fontWeight="semibold">
                            {parsedOfficeTask?.requestTypeLabel || 'Office Task'}
                        </Text>
                        {!!parsedOfficeTask?.priority && (
                            <Badge
                                variant="subtle"
                                colorScheme={getOfficeTaskPriorityColor(
                                    parsedOfficeTask.priority,
                                )}
                            >
                                {parsedOfficeTask.priorityLabel} Priority
                            </Badge>
                        )}
                    </Flex>
                    <Text fontSize="xs" color="gray.600" noOfLines={2}>
                        {parsedOfficeTask?.details ||
                            actionItem.description ||
                            'No details provided'}
                    </Text>
                    {formattedCallbackPhone && callbackPhone && (
                        <Text fontSize="xs" color="gray.600" mt={1}>
                            Phone:{' '}
                            <Box
                                as="a"
                                href={`tel:${cleanPhone(callbackPhone)}`}
                                color="brand.600"
                                _hover={{ textDecoration: 'underline' }}
                            >
                                {formattedCallbackPhone}
                            </Box>
                        </Text>
                    )}
                    <Text fontSize="xs" color="gray.500" mt={1}>
                        Created{' '}
                        {formatHumanReadableDate(actionItem.created_at || 0)}
                    </Text>
                </Box>
                <OfficeTaskActions actionItem={actionItem} isSmall />
            </Flex>
        </Box>
    )
}
